import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse, Box } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { ReactNode, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import theme, { ClickableIconSX } from "theme";

export interface CollapsibleSection {
  title: ReactNode;
  items: ReactNode[];
  expanded: boolean;
  selected?: boolean;
  disabled?: boolean;
  containerSX?: SystemStyleObject;
  onClick?: () => void;
}

const SectionUI = ({ section }: { section: CollapsibleSection }) => {
  const [open, setOpen] = useState<boolean>(section.expanded);

  const sx = section.selected
    ? {
        "a span": {
          color: `${theme.palette.grey["900"]}!important`,
          fontWeight: 900,
          textDecoration: "underline",
        },
      }
    : {};

  return (
    <Box sx={section.containerSX} onClick={section.onClick}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          ...sx,
          cursor: !section.disabled ? "pointer" : "default",
        }}
        /*
         Do not toggle the section if the section is disabled
         Do not close the section if the section has an onClick function
        */
        onClick={!section.disabled ? () => setOpen(section.onClick ? true : !open) : undefined}>
        {section.title}
        {!section.disabled && open ? (
          <ExpandLess
            sx={ClickableIconSX}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          />
        ) : !section.disabled ? (
          <ExpandMore
            sx={ClickableIconSX}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          />
        ) : (
          <></>
        )}
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: "flex", flexDirection: "column", "> *": { marginY: 1 } }}>
          {section.items.map((item) => item)}
        </Box>
      </Collapse>
    </Box>
  );
};

interface CollapsibleSectionsProps {
  sections: CollapsibleSection[];
}

interface VirtulizedCollapsibleSectionsProps extends CollapsibleSectionsProps {
  height: number;
}

const CollapsibleSections = ({ sections }: CollapsibleSectionsProps) => {
  return (
    <div>
      {sections.map((section, idx) => (
        <SectionUI key={idx} section={section} />
      ))}
    </div>
  );
};

export const VirtulizedCollapsibleSections = ({ height, sections }: VirtulizedCollapsibleSectionsProps) => {
  return (
    <div>
      <Virtuoso
        style={{ height: height }}
        data={sections}
        itemContent={(_, section) => <SectionUI section={section} />}
      />
    </div>
  );
};

export default CollapsibleSections;
